import { uploadToGCS, type GCSUploadOptions } from './gcs';
import { uploadToS3, type S3UploadOptions } from './s3';

export type { GCSUploadOptions } from './gcs';
export type { S3UploadOptions } from './s3';

/**
 * Signed-upload helpers.
 *
 * Ported from the Ember `utils/uploads` module. The backend's "sign" endpoints
 * return either a GCS or an S3 policy; `getUploadFn` picks the matching uploader
 * so callers can upload a file to signed storage without caring which datacenter
 * they're in.
 */

export type SignResponse = { gcs: GCSUploadOptions } | { s3: S3UploadOptions };

export type UploaderFunction = (
  file: File,
  json: GCSUploadOptions | S3UploadOptions,
  options: { processData?: (data: FormData) => void },
) => Promise<{ key: string }>;

export function getUploadFunction(
  signResponse: SignResponse,
  fileType?: string,
): { uploadFn: UploaderFunction; data: GCSUploadOptions | S3UploadOptions } {
  // Historically we've uploaded directly to S3; the EU datacenter uploads to
  // GCS. In the interim both are supported — the backend's sign response tells
  // us which bucket to use, so we branch on its shape.
  if ('gcs' in signResponse) {
    return { uploadFn: uploadToGCS as UploaderFunction, data: signResponse.gcs };
  }

  const data = { ...signResponse.s3 };
  if (fileType != null) {
    data['Content-Type'] = fileType;
  }

  return { uploadFn: uploadToS3 as UploaderFunction, data };
}
